import { Component } from '@theme/component';

/**
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} drawer
 * @property {HTMLFormElement[]} [dialogForms] - Forms with method="dialog" inside the drawer.
 */

/**
 * Base class for drawer components.
 * Provides common functionality for opening, closing, and animating drawers.
 * @abstract
 * @extends {Component<DrawerRefs>}
 */
export class Drawer extends Component {
  /** @type {string[]} */
  static requiredRefs = ['drawer'];

  /**
   * Override in subclass for CSS class name (e.g., 'cart-drawer', 'filter-drawer').
   * @type {string}
   */
  static drawerName = 'drawer';

  /** @type {boolean} */
  isClosing = false;

  /** @type {(() => void) | null} */
  originalClose = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {HTMLDialogElement | null} */
  #currentDialog = null;

  /** @type {boolean} */
  #mouseDownOnBackdrop = false;

  /**
   * Returns the drawer name for CSS class generation.
   * @returns {string}
   */
  get drawerName() {
    return /** @type {typeof Drawer} */ (this.constructor).drawerName;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setupDialogBehavior();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#setupDialogBehavior();

    // Réappliquer les classes d'ouverture si le drawer est ouvert (après un morphing)
    if (this.refs.drawer?.hasAttribute('open')) {
      this.refs.drawer.classList.add(`${this.drawerName}--opening`);
      this.refs.drawer.classList.add('drawer-shell--opening');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownDialogBehavior();
  }

  /**
   * Opens the drawer with animation.
   */
  openDrawer() {
    this.refs.drawer?.showModal();
  }

  /**
   * Closes the drawer with animation.
   */
  closeDrawer() {
    this.refs.drawer?.close();
  }

  #setupDialogBehavior() {
    const dialog = this.refs.drawer;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (this.#currentDialog === dialog) return;

    this.#teardownDialogBehavior();

    this.#currentDialog = dialog;
    this.originalClose = dialog.close.bind(dialog);
    const originalShowModal = dialog.showModal.bind(dialog);

    // Event listeners
    dialog.addEventListener('mousedown', this.#onDialogMouseDown);
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('close', this.#onDialogClose);
    dialog.addEventListener('cancel', this.#onDialogCancel);

    // Setup dialog forms (for close buttons)
    if (Array.isArray(this.refs.dialogForms)) {
      for (const form of this.refs.dialogForms) {
        form.addEventListener('submit', this.#onDialogFormSubmit);
      }
    }

    // MutationObserver for open attribute
    this.#dialogObserver = new MutationObserver(() => {
      if (!this.refs.drawer) return;

      if (this.refs.drawer.hasAttribute('open')) {
        this.isClosing = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#handleOpen();
          });
        });
      } else if (!this.isClosing) {
        this.#handleClose();
      }
    });

    this.#dialogObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Intercept showModal
    dialog.showModal = () => {
      if (!this.refs.drawer) return;
      this.refs.drawer.style.transform = 'translateX(100%)';
      originalShowModal();
    };

    // Intercept close
    dialog.close = () => {
      if (!this.refs.drawer || !this.originalClose) return;
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          this.originalClose?.();
        });
      } else {
        this.originalClose();
      }
    };

    // Ensure drawer starts hidden
    dialog.style.transform = 'translateX(100%)';
  }

  #teardownDialogBehavior() {
    if (this.#currentDialog) {
      this.#currentDialog.removeEventListener('mousedown', this.#onDialogMouseDown);
      this.#currentDialog.removeEventListener('click', this.#onDialogClick);
      this.#currentDialog.removeEventListener('close', this.#onDialogClose);
      this.#currentDialog.removeEventListener('cancel', this.#onDialogCancel);
    }

    if (Array.isArray(this.refs.dialogForms)) {
      for (const form of this.refs.dialogForms) {
        form.removeEventListener('submit', this.#onDialogFormSubmit);
      }
    }

    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#currentDialog = null;
  }

  /** @param {MouseEvent} event */
  #onDialogMouseDown = (event) => {
    this.#mouseDownOnBackdrop = event.target === this.refs.drawer;
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    if (!this.refs.drawer || !this.originalClose) return;
    // Close only if both mousedown and click were on the backdrop
    if (event.target === this.refs.drawer && this.#mouseDownOnBackdrop) {
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          this.originalClose?.();
        });
      }
    }
    this.#mouseDownOnBackdrop = false;
  };

  #onDialogClose = () => {
    this.#handleClose();
  };

  /** @param {Event} event */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.closeDrawer();
  };

  /** @param {Event} event */
  #onDialogFormSubmit = (event) => {
    if (this.refs.drawer?.hasAttribute('open') && this.originalClose) {
      event.preventDefault();
      this.#animateClose(() => {
        this.originalClose?.();
      });
    }
  };

  #handleOpen() {
    if (!this.refs.drawer) return;

    this.refs.drawer.style.right = '0';
    this.refs.drawer.style.left = 'auto';
    this.refs.drawer.style.transform = 'translateX(100%)';

    // Force reflow
    this.refs.drawer.offsetHeight;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.refs.drawer) return;
        this.refs.drawer.style.transform = 'translateX(0)';
        this.refs.drawer.classList.add(`${this.drawerName}--opening`);
        this.refs.drawer.classList.add('drawer-shell--opening');
      });
    });
  }

  #handleClose() {
    if (!this.refs.drawer) return;
    this.refs.drawer.classList.remove(`${this.drawerName}--opening`);
    this.refs.drawer.classList.remove('drawer-shell--opening');
    this.refs.drawer.style.transform = 'translateX(100%)';
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    if (!this.refs.drawer) return;

    this.isClosing = true;
    this.refs.drawer.classList.remove(`${this.drawerName}--opening`);
    this.refs.drawer.classList.remove('drawer-shell--opening');
    this.refs.drawer.style.transform = 'translateX(100%)';

    setTimeout(() => {
      callback();
      this.isClosing = false;
    }, 300);
  }
}

/**
 * Creates and registers a simple drawer custom element.
 * @param {string} tagName - The custom element tag name (e.g., 'ingredients-drawer')
 * @param {string} [drawerName] - The drawer name for CSS classes (defaults to tagName)
 */
export function defineDrawer(tagName, drawerName) {
  if (customElements.get(tagName)) return;

  const name = drawerName || tagName;

  class SimpleDrawer extends Drawer {
    static drawerName = name;
  }

  customElements.define(tagName, SimpleDrawer);
}

// Register drawers
defineDrawer('ingredients-drawer');
defineDrawer('accordion-drawer');
defineDrawer('button-drawer');
